import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { eventFiles } from '@lexical/rich-text'
import { mergeRegister } from '@lexical/utils'
import { $getNearestNodeFromDOMNode, $getNodeByKey, COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_LOW, DRAGOVER_COMMAND, DROP_COMMAND } from 'lexical'
import React, { FC } from 'react'
import { DragEvent as ReactDragEvent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { makeStyles } from 'tss-react/mui'
import { Box } from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'

import { isHTMLElement } from '../../utils/guard'
import { getBlockElement, hideTargetLine, isOnMenu, setDragImage, setMenuPosition, setTargetLine } from './helpers'
import { DRAG_DATA_FORMAT } from './const'

const useStyles = makeStyles()(theme => ({
  dragableButon: {
    borderRadius: theme.shape.markdownEditor.draggableButtonBorderRadius,
    padding: theme.spacing(0.5),
    cursor: 'grab',
    opacity: 0,
    position: 'absolute',
    left: '0',
    top: '0',
    willChange: 'transform',
    ':hover': {
      backgroundColor: theme.shape.markdownEditor.draggableButtonBackground,
    },
  },
  dragIcon: {
    fontSize: 14,
    opacity: 0.3,
    pointerEvents: 'none',
    height: 'auto',
  },
  targetLine: {
    pointerEvents: 'none',
    background: 'deepskyblue',
    height: '4px',
    position: 'absolute',
    left: '0',
    top: '0',
    opacity: 0,
    willChange: 'transform',
  },
}))

interface DraggableBlockPluginProps {
  anchorElem: HTMLElement
}

/**
 * Custom plugin for reordering any elements in the markdown editor
 */
export const DraggableBlockPlugin: FC<DraggableBlockPluginProps> = ({ anchorElem }) => {
  const [editor] = useLexicalComposerContext()
  const prevIndex = useRef<number>(Infinity)
  const { classes } = useStyles()

  const scrollerElem = anchorElem.parentElement

  const menuRef = useRef<HTMLDivElement>(null)
  const targetLineRef = useRef<HTMLDivElement>(null)
  const isDraggingBlockRef = useRef<boolean>(false)
  const [draggableBlockElem, setDraggableBlockElem] = useState<HTMLElement | null>(null)

  const setPrevIndex = (index: number): void => {
    prevIndex.current = index
  }

  useEffect(() => {
    const onMouseMove = (event: MouseEvent): void => {
      if (!isHTMLElement(event.target)) {
        setDraggableBlockElem(null)
        return
      }

      const target = event.target as HTMLElement

      if (isOnMenu(target)) {
        return
      }

      const _draggableBlockElem = getBlockElement({
        anchorElem,
        editor,
        event,
        prevIndex: prevIndex.current,
        setPrevIndex,
      })

      setDraggableBlockElem(_draggableBlockElem)
    }

    const onMouseLeave = (): void => {
      setDraggableBlockElem(null)
    }

    scrollerElem?.addEventListener('mousemove', onMouseMove)
    scrollerElem?.addEventListener('mouseleave', onMouseLeave)

    return () => {
      scrollerElem?.removeEventListener('mousemove', onMouseMove)
      scrollerElem?.removeEventListener('mouseleave', onMouseLeave)
    }
  }, [scrollerElem, anchorElem, editor])

  useEffect(() => {
    if (menuRef.current) {
      setMenuPosition(draggableBlockElem, menuRef.current, anchorElem)
    }
  }, [anchorElem, draggableBlockElem])

  useEffect(() => {
    const onDragover = (event: DragEvent): boolean => {
      if (!isDraggingBlockRef.current) {
        return false
      }
      const [isFileTransfer] = eventFiles(event)
      if (isFileTransfer) {
        return false
      }
      const { pageY, target } = event
      if (!isHTMLElement(target)) {
        return false
      }
      const targetBlockElem = getBlockElement({
        anchorElem,
        editor,
        event,
        useEdgeAsDefault: true,
        prevIndex: prevIndex.current,
        setPrevIndex,
      })
      const targetLineElem = targetLineRef.current
      if (targetBlockElem === null || targetLineElem === null) {
        return false
      }
      setTargetLine(targetLineElem, targetBlockElem, pageY, anchorElem)
      // Prevent default event to be able to trigger onDrop events
      event.preventDefault()
      return true
    }

    const onDrop = (event: DragEvent): boolean => {
      if (!isDraggingBlockRef.current) {
        return false
      }
      const [isFileTransfer] = eventFiles(event)
      if (isFileTransfer) {
        return false
      }
      const { target, dataTransfer, pageY } = event
      const dragData = dataTransfer?.getData(DRAG_DATA_FORMAT) || ''
      const draggedNode = $getNodeByKey(dragData)
      if (!draggedNode) {
        return false
      }
      if (!isHTMLElement(target)) {
        return false
      }
      const targetBlockElem = getBlockElement({
        anchorElem,
        editor,
        event,
        useEdgeAsDefault: true,
        prevIndex: prevIndex.current,
        setPrevIndex,
      })
      if (!targetBlockElem) {
        return false
      }
      const targetNode = $getNearestNodeFromDOMNode(targetBlockElem)
      if (!targetNode) {
        return false
      }
      if (targetNode === draggedNode) {
        return true
      }
      const targetBlockElemTop = targetBlockElem.getBoundingClientRect().top
      if (pageY >= targetBlockElemTop) {
        targetNode.insertAfter(draggedNode)
      } else {
        targetNode.insertBefore(draggedNode)
      }
      setDraggableBlockElem(null)

      return true
    }

    return mergeRegister(
      editor.registerCommand(
        DRAGOVER_COMMAND,
        event => {
          return onDragover(event)
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        DROP_COMMAND,
        event => {
          return onDrop(event)
        },
        COMMAND_PRIORITY_HIGH
      )
    )
  }, [anchorElem, editor])

  const onDragStart = (event: ReactDragEvent<HTMLDivElement>): void => {
    const dataTransfer = event.dataTransfer
    if (!dataTransfer || !draggableBlockElem) {
      return
    }
    setDragImage(dataTransfer, draggableBlockElem)
    let nodeKey = ''
    editor.update(() => {
      const node = $getNearestNodeFromDOMNode(draggableBlockElem)
      if (node) {
        nodeKey = node.getKey()
      }
    })
    isDraggingBlockRef.current = true
    dataTransfer.setData(DRAG_DATA_FORMAT, nodeKey)
  }

  const onDragEnd = (): void => {
    isDraggingBlockRef.current = false
    hideTargetLine(targetLineRef.current)
  }

  return createPortal(
    <>
      <Box className={classes.dragableButon} ref={menuRef} draggable={true} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <DragIndicatorIcon className={classes.dragIcon} />
      </Box>
      <Box className={classes.targetLine} ref={targetLineRef} />
    </>,
    anchorElem
  )
}
